import { useEffect, useMemo, useState } from "react";
import { Card, DataTable, Badge, Banner, Button, Text, Link, Tooltip, EmptyState, BlockStack, InlineStack } from "@shopify/polaris";
import { getRequest, formatToCurrency } from "../../../shared/components/functions";
import { buildWorkbookXml, downloadWorkbook, excelCell, excelRow } from "../../../shared/components/excelExport";

// MRP ordering plan for one supplier: what must be ordered now, and what is
// coming up in the horizon — so a single order can cover both. Data comes from
// the latest persisted MRP run (/api/mrp/supplier/:id/plan).

const ORDER_NOW_WINDOW_DAYS = 7;

const addDaysIso = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().substring(0, 10);
};

const buildSheetRows = (title, parts) => {
  const rows = [
    excelRow([excelCell(title, "Title")]),
    excelRow([excelCell(`Generated ${new Date().toISOString().substring(0, 10)}`)]),
    "<Row />",
    excelRow([
      excelCell("Part ID", "Header"),
      excelCell("Reference", "Header"),
      excelCell("Description", "Header"),
      excelCell("On hand", "Header"),
      excelCell("Incoming", "Header"),
      excelCell("Qty to order", "Header"),
      excelCell("Order by", "Header"),
      excelCell("First needed", "Header"),
      excelCell("Unit price", "Header"),
      excelCell("Value", "Header"),
    ]),
  ];
  parts.forEach((part) => {
    rows.push(
      excelRow([
        excelCell(part.productId, undefined, true),
        excelCell(part.productRef || ""),
        excelCell(part.productName || ""),
        excelCell(part.onHandQty ?? "", undefined, true),
        excelCell(part.incomingQty ?? "", undefined, true),
        excelCell(part.totalSuggestedQty, undefined, true),
        excelCell(part.firstOrderByDate || "", part.isLate ? "Late" : undefined),
        excelCell(part.firstNeedDate || ""),
        excelCell(part.unitPrice ?? "", undefined, true),
        excelCell(part.totalValue, undefined, true),
      ])
    );
  });
  return rows.join("");
};

const exportPlan = (supplierName, orderNow, comingUp) => {
  const xml = buildWorkbookXml([
    { name: "Order now", rowsXml: buildSheetRows(`${supplierName} — order now`, orderNow) },
    { name: "Coming up", rowsXml: buildSheetRows(`${supplierName} — coming up`, comingUp) },
  ]);
  downloadWorkbook(xml, `order-plan-${String(supplierName).replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}-${new Date().toISOString().substring(0, 10)}.xls`);
};

const copyProposal = async (parts, setToast) => {
  const lines = parts.map((part) => `${part.productId}\t${part.productRef || ""}\t${part.productName || ""}\t${part.totalSuggestedQty}\torder by ${part.firstOrderByDate || "?"}`);
  try {
    await navigator.clipboard.writeText(lines.join("\n"));
    if (setToast) setToast("ok");
  } catch (err) {
    if (setToast) setToast("error");
  }
};

const partsTable = (parts) => (
  <DataTable
    verticalAlign="middle"
    increasedTableDensity
    columnContentTypes={["text", "text", "text", "numeric", "numeric", "numeric", "text", "text", "numeric"]}
    headings={[<b>Part</b>, <b>Reference</b>, <b>Description</b>, <b>On hand</b>, <b>Incoming</b>, <b>Qty to order</b>, <b>Order by</b>, <b>First needed</b>, <b>Value</b>]}
    rows={parts.map((part) => [
      <Link removeUnderline url={`/products/${part.productId}`}>
        {part.productId}
      </Link>,
      part.productRef || "",
      String(part.productName || "").substring(0, 45),
      part.onHandQty ?? "",
      part.incomingQty ? (
        <Tooltip content={part.nextIncomingDate ? `Next receipt ${part.nextIncomingDate}` : "No confirmed date"}>
          <Text as="span">{part.incomingQty}</Text>
        </Tooltip>
      ) : (
        0
      ),
      <Text as="span" fontWeight="bold">
        {part.totalSuggestedQty}
      </Text>,
      part.isLate ? (
        <InlineStack gap="100" blockAlign="center">
          {part.firstOrderByDate || ""}
          <Badge tone="critical">Late</Badge>
        </InlineStack>
      ) : (
        part.firstOrderByDate || ""
      ),
      part.firstNeedDate || "",
      part.totalValue ? formatToCurrency(part.totalValue) : "",
    ])}
  />
);

export default function MrpCompanyPlan({ FIRNR, supplierName, setToast }) {
  const [plan, setPlan] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function fetchPlan() {
      const data = await getRequest(`/api/mrp/supplier/${FIRNR}/plan`);
      setPlan(data);
      setLoaded(true);
    }
    fetchPlan();
  }, [FIRNR]);

  const { orderNow, comingUp } = useMemo(() => {
    const parts = plan?.parts || [];
    const cutoff = addDaysIso(ORDER_NOW_WINDOW_DAYS);
    return {
      orderNow: parts.filter((part) => part.isLate || (part.firstOrderByDate && part.firstOrderByDate <= cutoff)),
      comingUp: parts.filter((part) => !(part.isLate || (part.firstOrderByDate && part.firstOrderByDate <= cutoff))),
    };
  }, [plan]);

  if (!loaded) return null;

  if (!plan?.run) {
    return (
      <Banner title="No MRP run available" tone="info">
        <p>
          No completed purchase-planner run yet. Start one from the <Link url="/mrp">purchase planner</Link>.
        </p>
      </Banner>
    );
  }

  if (!plan.parts.length) {
    return (
      <Card>
        <EmptyState heading="Nothing to order from this supplier" image="">
          <p>The latest MRP run ({String(plan.run.finishedAt).replace("T", " ").substring(0, 16)}) has no net requirements for this supplier in the next {plan.run.horizonDays} days.</p>
        </EmptyState>
      </Card>
    );
  }

  const totalValue = plan.parts.reduce((sum, part) => sum + (part.totalValue || 0), 0);

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" tone="subdued">{`Based on MRP run of ${String(plan.run.finishedAt).replace("T", " ").substring(0, 16)} · horizon ${plan.run.horizonDays} days`}</Text>
          <Badge tone="info">{formatToCurrency(totalValue)}</Badge>
        </InlineStack>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => copyProposal([...orderNow, ...comingUp], setToast)}>
            Copy proposal
          </Button>
          <Button size="slim" onClick={() => exportPlan(supplierName, orderNow, comingUp)}>
            Export Excel
          </Button>
        </InlineStack>
      </InlineStack>

      <Card>
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Order now
            </Text>
            <Badge tone={orderNow.length ? "critical" : "success"}>{`${orderNow.length} part${orderNow.length === 1 ? "" : "s"}`}</Badge>
          </InlineStack>
          <Text as="p" tone="subdued" variant="bodySm">
            Late, or must be ordered within {ORDER_NOW_WINDOW_DAYS} days to arrive on time.
          </Text>
          {orderNow.length ? partsTable(orderNow) : <Text as="p" tone="subdued">Nothing urgent for this supplier.</Text>}
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="200">
          <InlineStack gap="200" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Coming up
            </Text>
            <Badge>{`${comingUp.length} part${comingUp.length === 1 ? "" : "s"}`}</Badge>
          </InlineStack>
          <Text as="p" tone="subdued" variant="bodySm">
            Needed later in the horizon — consider adding these to the order above to save on shipping and handling.
          </Text>
          {comingUp.length ? partsTable(comingUp) : <Text as="p" tone="subdued">No future requirements for this supplier in the horizon.</Text>}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
